import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  withCredentials: true,
  timeout: 30_000,
});

type PendingRequest = {
  resolve: (token?: string) => void;
  reject: (err: unknown) => void;
};

let isRefreshing = false;
let queue: PendingRequest[] = [];

function flushQueue(err: unknown | null) {
  queue.forEach(({ resolve, reject }) => (err ? reject(err) : resolve()));
  queue = [];
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const onUnauthorizedSubscribers: Array<() => void> = [];

/** Frontend pode se inscrever para limpar AuthContext quando refresh falha. */
export function onUnauthorized(cb: () => void) {
  onUnauthorizedSubscribers.push(cb);
  return () => {
    const idx = onUnauthorizedSubscribers.indexOf(cb);
    if (idx >= 0) onUnauthorizedSubscribers.splice(idx, 1);
  };
}

function notifyUnauthorized() {
  for (const cb of onUnauthorizedSubscribers) {
    try {
      cb();
    } catch {
      // ignora
    }
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    if (!original) throw error;

    const status = error.response?.status;
    const url = original.url ?? '';
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout') ||
      url.includes('/invites/accept') ||
      url.includes('/invites/validate') ||
      url.includes('/password-resets/consume');

    if (status === 401 && !original._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Enfileira: dispara retry quando o refresh em curso terminar.
        return new Promise((resolve, reject) => {
          queue.push({
            resolve: () => {
              original._retry = true;
              resolve(api.request(original as AxiosRequestConfig));
            },
            reject: (err) => reject(err),
          });
        });
      }

      original._retry = true;
      isRefreshing = true;
      try {
        await api.post('/auth/refresh');
        flushQueue(null);
        return api.request(original as AxiosRequestConfig);
      } catch (refreshErr) {
        flushQueue(refreshErr);
        notifyUnauthorized();
        throw refreshErr;
      } finally {
        isRefreshing = false;
      }
    }

    throw error;
  },
);

export interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: unknown;
  requestId?: string;
}

export function getApiErrorMessage(err: unknown, fallback = 'Erro inesperado.'): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.message) return body.message;
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
